// =============================================================================
// domain/dayReport.ts — the shared day-report figures.
// -----------------------------------------------------------------------------
// Relocated from the old client's `dayReportSections`, DATA ONLY (D81). Its own
// header explains why that function exists, and the reason ports directly:
//
//   "Day Overview renders them for a calendar day of live trades; Practice
//    renders them for one imported session's copies. EXTRACTED rather than
//    duplicated so the two can't drift."
//
// ── 🚨 PURE: NO DATES, NO ACCOUNTS, NO REGISTRIES ──
//
// Beta is explicit that everything calendar- or account-shaped — the prop rails,
// the by-account table, the plan recap — deliberately stays OUT, because none of
// it means anything for a replay session. `domain/dayAccounts.ts` holds those.
// Folding them together here would make the Practice tab depend on a day.
//
// ── 🚨 FOUR POPULATIONS ON ONE SCREEN, AND THEY DISAGREE ON PURPOSE (D77/D80) ──
//
// This is the densest instance of it in the client so far. Reading beta's source:
//
//   `trades`   every trade of the day        → the KPIs, the curve, Controllable
//   `counted`  …filtered by `countsFn`       → Net P&L, give-back, Avg Time
//
// ⚠ CONTROLLABLE LOSSES COUNTS **ALL** TRADES, not the counted ones —
// `trades.reduce(...)` in beta, one line below a block that uses `counted`. A
// mistake made in a SIM account is still a mistake you made, and that is the
// honest reading of a discipline metric; it is not an edge claim. Ported as-is.
// =============================================================================

import { peakExposure, type Execution } from './capture';
import {
  dollarsPerPointFor, excursionValue, tradeValue, type ContractSpec,
} from './trade';
import { hmToMins } from './dayView';
import type { PnlUnit } from './types';

// ---------------------------------------------------------------------------
// Controllable losses
// ---------------------------------------------------------------------------

/**
 * The four negative booleans that auto-classify a loss as controllable.
 *
 * ⚠ `stopNotHonored` was added in beta v2.62 with its own note — "an unhonored
 * stop that deepened the loss is controllable". It is not one of the original
 * three and is easy to leave out when transcribing.
 */
export const CONTROLLABLE_BOOLEANS: readonly { key: string; reason: string }[] = [
  { key: 'revengeTrade', reason: 'revenge trade' },
  { key: 'fomoTrade', reason: 'FOMO trade' },
  { key: 'overtraded', reason: 'overtraded' },
  { key: 'stopNotHonored', reason: 'stop not honored' },
];

export interface ControllableTrade {
  netPnl: number | null;
  booleans?: Record<string, boolean> | null;
  mistakeTags?: readonly string[] | null;
}

/**
 * Is this mistake tag one the trader could have controlled?
 *
 * ── ⚠ ABSENT MEANS TRUE, AND INVERTING THAT SILENTLY SHRINKS THE METRIC ──
 *
 * The config is a sparse map `{ tagName: boolean }` holding only the tags the
 * user has explicitly opted OUT of. Beta's own comment: *"No explicit setting —
 * default to TRUE so newly-added tags participate automatically."* Reading a
 * missing key as false would make every tag the user never visited in Settings
 * stop counting, and Controllable Losses would read $0 on a day full of tagged
 * mistakes — a number that looks like good news.
 */
export function isMistakeTagControllable(
  tag: string,
  cfg: Record<string, unknown> | null | undefined,
): boolean {
  if (!tag) return false;
  const c = cfg ?? {};
  if (Object.prototype.hasOwnProperty.call(c, tag)) return c[tag] !== false;
  return true;
}

/** Human-readable reasons the auto-classifier found. Empty ≠ not controllable. */
export function controllableLossReasons(
  t: ControllableTrade,
  cfg: Record<string, unknown> | null | undefined,
): string[] {
  const out: string[] = [];
  const b = t.booleans ?? {};
  for (const { key, reason } of CONTROLLABLE_BOOLEANS) if (b[key]) out.push(reason);
  for (const tag of t.mistakeTags ?? []) if (isMistakeTagControllable(tag, cfg)) out.push(tag);
  return out;
}

/**
 * ⚠ A WINNER IS NEVER A CONTROLLABLE LOSS, however sloppy it was. Beta says so
 * in a comment — "a sloppy win is a different category" — and the check is the
 * first line, before any tag is read. Without it a revenge trade that happened
 * to work would subtract from the day's avoidable damage.
 *
 * ⚠ `netPnl >= 0`, so a SCRATCH is not a loss either.
 */
export function isControllableLoss(
  t: ControllableTrade,
  cfg: Record<string, unknown> | null | undefined,
): boolean {
  if ((Number(t.netPnl) || 0) >= 0) return false;
  // The manual override wins outright — the user overruling the classifier is
  // the whole point of the checkbox.
  if (t.booleans?.['controllableLoss'] === true) return true;
  return controllableLossReasons(t, cfg).length > 0;
}

// ---------------------------------------------------------------------------
// Give-back
// ---------------------------------------------------------------------------

export interface GiveBackTrade {
  contract: string | null;
  side: string | null;
  qty: number | null;
  points: number | null;
  netPnl: number | null;
  grossPnl?: number | null;
  fees?: number | null;
  rrRealized: number | null;
  riskPoints: number | null;
  mfePoints: number | null;
  mfeSecs?: number | null;
  executions?: readonly Execution[] | null;
}

export interface GiveBack {
  value: number;
  /**
   * False when the fill timeline was unusable and the full final quantity had to
   * be assumed on at the peak — an UPPER BOUND. The tile prefixes `≈`.
   */
  exact: boolean;
}

/**
 * What this trade handed back from its in-trade peak, in the display unit.
 *
 * ── 🚨 THE DOLLAR PATH IS SIZE-WEIGHTED; THE OTHERS ARE PER-CONTRACT ──
 *
 * In points / ticks / R the answer is `MFE − realized` per contract and there is
 * nothing to weight, so it is always exact. In dollars the size actually on at
 * the peak matters: a 5-lot scaled down to 1 before the high did not hand back
 * five lots' worth. That reconstruction is `peakExposure`, and when it cannot
 * reconcile the fills beta falls back to assuming the full final quantity — an
 * upper bound, flagged rather than hidden.
 *
 * ⚠ THE FALLBACK'S REALIZED LEG IS **GROSS**, not net. The exact path measures a
 * fee-free price move (peak − exit), so netting fees into only one of the two
 * would make the two paths disagree by the commission on every trade.
 */
export function giveBackForTrade(
  t: GiveBackTrade,
  unit: PnlUnit,
  catalogue: readonly ContractSpec[],
): GiveBack {
  const mfePoints = t.mfePoints;

  if (unit !== 'usd') {
    const mfeV = excursionValue(t, unit, mfePoints, catalogue);
    const realV = tradeValue(t, unit, catalogue);
    if (mfeV == null || realV == null) return { value: 0, exact: true };
    return { value: Math.max(0, mfeV - realV), exact: true };
  }

  const dpp = dollarsPerPointFor(t.contract, catalogue);
  const exposure = peakExposure(
    { ...t, mfeSecs: t.mfeSecs ?? null, executions: t.executions ?? null } as never,
    dpp,
  );
  if (exposure) return { value: exposure.giveBackUsd, exact: true };

  const mfeV = excursionValue(t, 'usd', mfePoints, catalogue);
  if (mfeV == null) return { value: 0, exact: false };
  // ⚠ Legacy rows predating `gross_pnl` fall back to net + fees, which is the
  // same quantity reconstructed. Beta does this and dropping it would make every
  // pre-v2 trade contribute its commission as give-back.
  const realV = t.grossPnl != null
    ? Number(t.grossPnl)
    : (Number(t.netPnl) || 0) + (Number(t.fees) || 0);
  return { value: Math.max(0, mfeV - realV), exact: false };
}

// ---------------------------------------------------------------------------
// The scorecard
// ---------------------------------------------------------------------------

export interface ScorecardTrade extends GiveBackTrade, ControllableTrade {
  timeIn: string | null;
  timeOut: string | null;
}

export interface Scorecard {
  /** Net P&L in the display unit, over the COUNTED trades. */
  netPnl: number;
  /**
   * The SAME figure in dollars, always, whatever the unit switch says.
   *
   * 🚨 FOR THE PROP RAILS AND NOTHING ELSE (#67). The daily-loss-limit rail
   * prints the day's close directly beside the firm's dollar limit — "closed
   * −140 of −2,000" — so that close has to be in the limit's own currency. On
   * Points it was printing a POINTS number with a dollar sign next to a real
   * risk limit, which is the one place on this page a wrong number can cost
   * money rather than mislead.
   *
   * ⚠ COMPUTED HERE, in the same loop, so it cannot drift from `netPnl`'s
   * population — same `counted` set, same skips. Summing it again at the rail
   * is how the two figures start disagreeing on one screen.
   */
  netPnlUsd: number;
  /** Median MFE captured, 0–100. Null when no trade reached 1R. */
  capturePct: number | null;
  /** How many ≥1R setups the median was taken over. */
  captureN: number;
  /** Total handed back, POSITIVE. Rendered negative. */
  giveBack: number;
  /** How many trades contributed. Zero renders "no give-back". */
  giveBackN: number;
  /** True when any contributor fell back to the qty assumption — renders `≈`. */
  giveBackApprox: boolean;
  /** Avoidable damage, POSITIVE, over ALL trades. See the header. */
  controllableLoss: number;
}

/**
 * ⚠ TAKES BOTH POPULATIONS EXPLICITLY rather than filtering internally. The
 * caller already knows which trades count — Day Overview uses `countsForMetrics`
 * and Practice needs its own, because practice copies keep their source trade's
 * `accountId` and the account-exclusion rule would drop every one of them.
 *
 * Passing both also makes the four-population split visible at the call site
 * instead of buried in a predicate argument.
 */
export function dayScorecard(
  trades: readonly ScorecardTrade[],
  counted: readonly ScorecardTrade[],
  unit: PnlUnit,
  catalogue: readonly ContractSpec[],
  capture: { pct: number | null; n: number },
  controllableCfg: Record<string, unknown> | null | undefined,
): Scorecard {
  let netPnl = 0;
  let netPnlUsd = 0;
  for (const t of counted) {
    const v = tradeValue(t, unit, catalogue);
    if (v != null) netPnl += v;
    // ⚠ A SECOND CALL rather than reusing `v`. When the unit already is dollars
    // the two agree; when it is not, this is the only one the rails may print.
    const usd = tradeValue(t, 'usd', catalogue);
    if (usd != null) netPnlUsd += usd;
  }

  let giveBack = 0, giveBackN = 0, giveBackApprox = false;
  for (const t of counted) {
    // ⚠ A trade with NO MFE AT ALL is skipped, not counted as zero give-back.
    // Beta's `continue`. Counting it would put "0 given back" in the denominator
    // of a metric whose subtitle is a trade COUNT, so the tile would read
    // "left on the table · 9 trades" on a day with two.
    if (t.mfePoints == null) continue;
    const gb = giveBackForTrade(t, unit, catalogue);
    if (gb.value > 0) {
      giveBack += gb.value;
      giveBackN++;
      if (!gb.exact) giveBackApprox = true;
    }
  }

  let controllableLoss = 0;
  for (const t of trades) {
    if (!isControllableLoss(t, controllableCfg)) continue;
    const v = tradeValue(t, unit, catalogue);
    if (v != null) controllableLoss += Math.abs(v);
  }

  return {
    netPnl,
    netPnlUsd,
    capturePct: capture.pct,
    captureN: capture.n,
    giveBack, giveBackN, giveBackApprox,
    controllableLoss,
  };
}

/** Beta's colour bands for the capture tile. Below 30 is red, 60+ green. */
export function captureTone(pct: number | null): 'green' | 'red' | '' {
  if (pct == null) return '';
  return pct >= 60 ? 'green' : pct >= 30 ? '' : 'red';
}

// ---------------------------------------------------------------------------
// The performance strip
// ---------------------------------------------------------------------------

/**
 * Mean minutes in trade, over the COUNTED set.
 *
 * ⚠ A trade whose exit clock is BEFORE its entry clock is dropped, not counted
 * as negative — that is an overnight hold or a bad import, and either way a
 * negative duration would drag the mean below zero and read as nonsense.
 */
export function avgMinutesInTrade(
  counted: readonly { timeIn: string | null; timeOut: string | null }[],
): number | null {
  const spans: number[] = [];
  for (const t of counted) {
    const a = hmToMins(t.timeIn), b = hmToMins(t.timeOut);
    if (a != null && b != null && b >= a) spans.push(b - a);
  }
  if (!spans.length) return null;
  return spans.reduce((s, x) => s + x, 0) / spans.length;
}

// ---------------------------------------------------------------------------
// Plan & Recap
// ---------------------------------------------------------------------------

/**
 * One labelled line of the Day Overview's Plan & Recap card.
 *
 * ⚠ A LIST, NOT A STRING. The old client builds HTML here
 * (`dvPlanRecapHtml`, index.html:27808) because it has nowhere else to put the
 * composition; returning data keeps the decision — which parts appear, in what
 * order, and when the card is suppressed entirely — testable without a DOM.
 */
export interface PlanRecapLine {
  /** "Regime", "Bias", "Plan", "Hypo 1", "What worked"… */
  label: string;
  text: string;
}

export interface PlanRecapHypothesis {
  setup: string;
  patterns: readonly { id: string; name: string }[];
}

export interface PlanRecapEntry {
  preMarketPlan: {
    marketRegime: string;
    gammaRegime: string;
    overallBias: string;
    biasSummary: string;
    marketRegimeAnalysis: string;
    hypotheses: readonly PlanRecapHypothesis[];
  };
  eodRecap: {
    whatWorked: string;
    whatBroke: string;
    lessons: string;
    tomorrowFocus: string;
  };
}

/**
 * The day's plan and recap, reduced to the lines the old client prints.
 *
 * ── 🚨 IT IS A NARROW SLICE, AND THE OMISSIONS ARE THE OLD CLIENT'S ──
 *
 * `gammaRegimeAnalysis`, the disqualifiers, the self-assessment, the key levels,
 * `eodRecap.wins` and `eodRecap.mistakes` are all stored, all displayed on the
 * Journal tab, and NONE of them appear here. That is beta's selection, kept
 * because this card is a reminder of what the day was FOR, not a second copy of
 * the journal — and widening it is a product decision, not a port fix.
 *
 * ⚠ `wins` / `mistakes` being absent also sidesteps D60 entirely: those are the
 * two columns stored inverted on disk. Nothing here reads either.
 *
 * ── 🚨 THE PATTERN NAME IS RESOLVED LIVE, WITH THE STORED NAME AS FALLBACK ──
 *
 * Beta does `state.patterns.find(x => x.id === p.id)?.name || p.name || '?'`.
 * The order matters: a pattern renamed since the plan was written should read
 * with its CURRENT name, because that is what the trader will look for on the
 * Patterns tab — but a pattern DELETED since then must still show what it was
 * called, which is why the stored copy is kept on the row at all.
 *
 * @param entry  the day's journal entry, or null for a day with no entry
 * @param patternNames  id → current name. Empty map is fine; the fallbacks hold.
 */
export function planRecapLines(
  entry: PlanRecapEntry | null,
  patternNames: ReadonlyMap<string, string>,
): readonly PlanRecapLine[] {
  if (!entry) return [];
  const p = entry.preMarketPlan;
  const r = entry.eodRecap;
  const out: PlanRecapLine[] = [];

  const push = (label: string, text: string) => {
    if (text && text.trim()) out.push({ label, text: text.trim() });
  };

  // ⚠ The triad is JOINED, not three lines, and the suffixes are beta's:
  // "BALANCED/RANGING · POSITIVE gamma · BEAR/NEUTRAL bias". Without them the
  // three values are unlabelled strings in a row and only the author knows
  // which is which.
  const triad = [
    p.marketRegime,
    p.gammaRegime ? `${p.gammaRegime} gamma` : '',
    p.overallBias ? `${p.overallBias} bias` : '',
  ].filter((s) => s && s.trim());
  if (triad.length) out.push({ label: 'Regime', text: triad.join(' · ') });

  push('Bias', p.biasSummary);
  push('Plan', p.marketRegimeAnalysis);

  // ⚠ NUMBERED BY POSITION IN THE STORED ARRAY, including any that are skipped.
  // "Hypo 1" and "Hypo 3" with no 2 is correct — it says the middle hypothesis
  // was left blank, which is information. Renumbering the survivors 1 and 2
  // would quietly claim there were only ever two.
  p.hypotheses.forEach((h, i) => {
    const names = h.patterns
      .map((x) => patternNames.get(x.id) || x.name || '?')
      .join(' · ');
    const setup = (h.setup || '').trim();
    if (!names && !setup) return;
    out.push({
      label: `Hypo ${i + 1}`,
      text: (names || '(no patterns)') + (setup ? ` — ${setup}` : ''),
    });
  });

  push('What worked', r.whatWorked);
  push('What broke', r.whatBroke);
  push('Lessons', r.lessons);
  push("Tomorrow's focus", r.tomorrowFocus);

  return out;
}
